import { useCallback, useRef, useState } from '@frontend/react';
import {
  useAnalytics,
  useCreateSetupIntentMutation,
  useRetrieveClientQuery,
  useSetupIntentConfirmedMutation,
  useUpdatePlanMutation,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { addBreadcrumb } from '@sentry/react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentSuccessData } from './types';

export type UsePaymentCaptureParams = {
  amountCharged: Cent;
  nextBillingDate: DateISOString;
  onError: (error: string) => void;
  onSuccess: (data: PaymentSuccessData) => void;
  onboardingSessionId?: string;
  planCode: string;
  planPrice: Cent;
  promoCode?: string;
  step: 'paymentCapture';
};

export type UsePaymentCaptureReturn = {
  isProcessing: boolean;
  submitPayment: (paymentMethodId?: string) => Promise<boolean>;
};

const getNormalizedAnalyticsError = (error: unknown) => {
  const errorRecord = typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : undefined;
  const errorData =
    typeof errorRecord?.data === 'object' && errorRecord.data !== null
      ? (errorRecord.data as Record<string, unknown>)
      : undefined;
  const backendCode = typeof errorData?.code === 'number' ? errorData.code : undefined;
  const httpStatus = typeof errorRecord?.status === 'number' ? errorRecord.status : undefined;
  const errorCategory =
    backendCode === 4001
      ? 'validation'
      : httpStatus === 402
        ? 'payment'
        : typeof httpStatus === 'number' && httpStatus >= 500
          ? 'network'
          : 'unknown';

  return {
    backendCode,
    errorCategory,
    errorCode:
      backendCode !== undefined
        ? `ONBOARDING_MEMBERSHIP_PAYMENT_${backendCode}`
        : 'ONBOARDING_MEMBERSHIP_PAYMENT_SUBMIT_FAILED',
    errorSource: 'paymentSubmit',
    httpStatus,
    retriable: backendCode !== 4001,
  } as const;
};

/**
 * Hook that orchestrates the 4-step payment flow for subscription activation:
 * 1. createSetupIntent() → get Stripe client secret
 * 2. stripe.confirmCardSetup() → confirm card with Stripe
 * 3. setupIntentConfirmed() → save payment method to backend
 * 4. updatePlan() → activate subscription
 *
 * On success: calls onSuccess callback
 * On failure: calls onError callback with error message
 */
export const usePaymentCapture = ({
  amountCharged,
  nextBillingDate,
  onboardingSessionId,
  onError,
  onSuccess,
  planCode,
  planPrice,
  promoCode,
  step,
}: UsePaymentCaptureParams): UsePaymentCaptureReturn => {
  const { track, trackAdvertising } = useAnalytics();
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const inFlightRef = useRef(false);

  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [setupIntentConfirmed] = useSetupIntentConfirmedMutation();
  const [updatePlan] = useUpdatePlanMutation();

  const { data: client } = useRetrieveClientQuery();

  const submitPayment = useCallback(
    async (paymentMethodId?: string): Promise<boolean> => {
      if (inFlightRef.current) {
        return false;
      }

      if (!stripe || !client) {
        onError('Payment is not ready. Please wait a moment and try again.');
        return false;
      }

      if (!paymentMethodId) {
        if (!elements) {
          onError('Payment is not ready. Please wait a moment and try again.');
          return false;
        }
        if (!elements.getElement(CardNumberElement)) {
          onError('Card information is not ready');
          return false;
        }
      }

      const cardElement = !paymentMethodId && elements ? elements.getElement(CardNumberElement) : null;

      // eslint-disable-next-line fp/no-mutation -- Ref mutation for atomic duplicate-submit lock
      inFlightRef.current = true;
      setIsProcessing(true);

      track({
        data: {
          onboardingSessionId,
          planCode,
          platform: 'web',
          purchaseType: 'Subscription',
          step,
        },
        event: 'membershipPaymentSubmitAttempted',
        topic: 'onboarding',
      });

      const mutationResult = await handleMutation({
        errorBreadcrumb: {
          category: 'payment.submitPayment',
          message: 'Error occurred during payment submission',
        },
        mutation: async () => {
          const setupIntentResult = await createSetupIntent().unwrap();

          if (!setupIntentResult.stripeClientSecret) {
            throw new Error('Failed to create setup intent');
          }

          const paymentMethod = paymentMethodId
            ? { payment_method: paymentMethodId }
            : {
                payment_method: {
                  billing_details: {
                    email: client.email,
                    name: `${client.firstName} ${client.lastName}`,
                    phone: client.phoneNumber || undefined,
                  },
                  card: cardElement!,
                },
              };

          const { error: stripeError, setupIntent: confirmedSetupIntent } = await stripe.confirmCardSetup(
            setupIntentResult.stripeClientSecret,
            paymentMethod,
          );

          if (stripeError) {
            addBreadcrumb({
              category: 'payment.confirmCardSetup',
              level: 'warning',
              message: `code=${stripeError.code ?? 'unknown'} type=${stripeError.type} decline_code=${stripeError.decline_code ?? 'none'}`,
              type: 'info',
            });
            throw new Error(stripeError.message || 'Failed to confirm card with Stripe');
          }

          if (!confirmedSetupIntent) {
            throw new Error('No setup intent returned from Stripe');
          }

          await setupIntentConfirmed({
            isDefault: true,
            setupIntentId: confirmedSetupIntent.id,
            skipInvalidate: true,
          }).unwrap();

          await updatePlan({
            plan: planCode as Parameters<typeof updatePlan>[0]['plan'],
            promotionCode: promoCode,
          }).unwrap();

          return { amountCharged, nextBillingDate };
        },
        onComplete: () => {
          // eslint-disable-next-line fp/no-mutation -- Ref mutation to release duplicate-submit lock
          inFlightRef.current = false;
          setIsProcessing(false);
        },
        onError: (error) => {
          track({
            data: {
              ...getNormalizedAnalyticsError(error),
              onboardingSessionId,
              planCode,
              platform: 'web',
              purchaseType: 'Subscription',
              step,
            },
            event: 'membershipPaymentSubmitFailed',
            topic: 'onboarding',
          });

          const errorMessage = error instanceof Error ? error.message : 'Payment failed. Please try again.';
          onError(errorMessage);
        },
        onSuccess: (result) => {
          if (result) {
            if (paymentMethodId) {
              track({
                data: {
                  onboardingSessionId,
                  planCode,
                  platform: 'web',
                  purchaseType: 'Subscription',
                  step,
                },
                event: 'membershipApplePayCardAdded',
                topic: 'onboarding',
              });
            }

            track({
              data: {
                amountCharged,
                onboardingSessionId,
                planCode,
                planName: planCode,
                planPrice,
                platform: 'web',
                purchaseType: 'Subscription',
                step,
              },
              event: 'membershipPurchaseCompleted',
              topic: 'onboarding',
            });

            trackAdvertising('Purchase', {
              currency: 'USD',
              planCode,
              planName: planCode,
              purchaseType: 'Subscription',
              value: planPrice / 100,
            });

            onSuccess(result);
          }
        },
        showErrorToast: false,
        showSuccessToast: false,
      });

      return Boolean(mutationResult);
    },
    [
      stripe,
      elements,
      client,
      createSetupIntent,
      setupIntentConfirmed,
      updatePlan,
      planCode,
      planPrice,
      promoCode,
      amountCharged,
      nextBillingDate,
      onboardingSessionId,
      onSuccess,
      onError,
      step,
      track,
      trackAdvertising,
    ],
  );

  return {
    isProcessing,
    submitPayment,
  };
};
